import { usePathname } from 'next/navigation';
import { useUser } from '@descope/nextjs-sdk/client';
import { PAGES_WITHOUT_TRIAL_HEADER, PARENT_PAGES_WITHOUT_TRIAL_HEADER } from '@/constants/trial';
import { useUserSubscription } from '@/hooks/useUserSubscription';

export function useTrialHeaderBanner(
    userDetails?: { trialUsed?: boolean; trialEndDate?: string | Date | null } | null,
) {
    const { user } = useUser();
    const location = usePathname();

    const pathArray = location.split('/');
    const page = pathArray[pathArray.length - 1];
    const pageParent = pathArray.length > 1 ? pathArray[pathArray.length - 2] : '';
    const shouldHideOnPage =
        PAGES_WITHOUT_TRIAL_HEADER.includes(page as (typeof PAGES_WITHOUT_TRIAL_HEADER)[number]) ||
        PARENT_PAGES_WITHOUT_TRIAL_HEADER.includes(pageParent as (typeof PARENT_PAGES_WITHOUT_TRIAL_HEADER)[number]);

    const userId = user?.customAttributes?.['clearittyId'];
    const { data: subscriptionData } = useUserSubscription(
        !shouldHideOnPage && Boolean(userId) && Boolean(userDetails?.trialUsed),
    );
    const subscription = subscriptionData ?? null;

    const isTrialStatus =
        subscription?.status === 'trialing' ||
        subscription?.status === 'paused' ||
        subscription?.status === 'past_due';

    const isTrialEnded = subscription
        ? subscription.status === 'paused' ||
        subscription.status === 'past_due' ||
        subscription.current_period_end * 1000 <= Date.now()
        : false;

    // Only show trial banner for genuine free trials (started via start-trial endpoint),
    // not for paid subscriptions that happen to have a complimentary trial period.
    // trialUsed is only set to true by the start-trial flow.
    // Additionally, if the user later subscribes to a paid plan with its own trial period,
    // the subscription's period end will diverge from the original trialEndDate.
    const isGenuineFreeTrial = (() => {
        if (!userDetails?.trialUsed) return false;
        if (!userDetails.trialEndDate || !subscription) return true;
        const trialEndMs = new Date(userDetails.trialEndDate).getTime();
        const subscriptionEndMs = subscription.current_period_end * 1000;
        return Math.abs(subscriptionEndMs - trialEndMs) < 48 * 3600 * 1000;
    })();

    const showTrialHeader =
        !!subscription && !!isTrialStatus && isGenuineFreeTrial;

    return {
        subscription,
        showTrialHeader,
        isTrialEnded,
        shouldHideOnPage,
    };
}
